// Processing errors list one issue per row/field, joined by '; ' (see
// mmm-rawdata parseCsv), e.g.:
// "All sheets failed: Daily Digital: CSV validation errors: Row 2, field
// 'Spend': Required; Row 3, field 'Spend': Required; ..."
const MAX_ISSUES_SHOWN = 3;
const MAX_ISSUES_LENGTH = 300;

const ROW_ISSUE_PATTERN = /^(.*?)Row (\d+), (.+)$/;

type GroupedIssue = {
  prefix: string;
  text: string;
  rows: string[];
};

// The same problem (e.g. a missing column) repeats for every row of the
// sheet — collapse identical issue texts into one entry with a row list
const groupIssuesByText = (issueParts: string[]): GroupedIssue[] => {
  const grouped = new Map<string, GroupedIssue>();

  for (const part of issueParts) {
    const match = part.match(ROW_ISSUE_PATTERN);
    const prefix = match ? match[1] : '';
    const row = match ? match[2] : null;
    const text = match ? match[3] : part;

    const existing = grouped.get(text);
    if (existing) {
      if (row) existing.rows.push(row);
      // Keep the longest prefix (the first part carries the sheet context)
      if (prefix.length > existing.prefix.length) existing.prefix = prefix;
    } else {
      grouped.set(text, { prefix, text, rows: row ? [row] : [] });
    }
  }

  return Array.from(grouped.values());
};

const renderIssue = (issue: GroupedIssue): string => {
  if (issue.rows.length === 0) return `${issue.prefix}${issue.text}`;
  if (issue.rows.length === 1) {
    return `${issue.prefix}Row ${issue.rows[0]}, ${issue.text}`;
  }
  return `${issue.prefix}${issue.text} (${issue.rows.length} rows)`;
};

const truncateIssues = (errorMessage: string): string => {
  const grouped = groupIssuesByText(errorMessage.split('; '));
  const shown = grouped.slice(0, MAX_ISSUES_SHOWN);

  let text = shown.map(renderIssue).join('; ');
  if (text.length > MAX_ISSUES_LENGTH) {
    text = `${text.slice(0, MAX_ISSUES_LENGTH)}…`;
  }

  const hiddenCount = grouped.length - shown.length;
  if (hiddenCount > 0) {
    text += ` … and ${hiddenCount} more ${hiddenCount === 1 ? 'issue' : 'issues'}`;
  }

  return text;
};

type FailedUpload = {
  fileName: string;
  errorMessage?: string;
};

/**
 * Builds a toast description for failed uploads: the processing error from
 * the backend per file — identical row issues grouped, truncated to the
 * first few distinct issues — or a generic retry hint when no error detail
 * is available.
 */
export const formatFailedUploadsMessage = (
  failedUploads: FailedUpload[],
): string =>
  failedUploads
    .map((failed) =>
      failed.errorMessage
        ? `${failed.fileName}: ${truncateIssues(failed.errorMessage)}`
        : `${failed.fileName}: upload failed. Please try again.`,
    )
    .join(' — ');
